import { DataTableToolbar } from '@app/components/data-table/data-table-toolbar';
import { useServerDataTable } from '@app/components/data-table/hooks/use-server-data-table';
import { useServerFilterOptions } from '@app/components/data-table/hooks/use-server-filter-options';
import { toPickerPeriods } from '@app/components/multi-period-picker';
import { useGetSoldFoodAndDrinkList } from '@shared/api/hooks/sold-goods/food-and-drink';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';

/**
 * Toolbar for the sold-goods Excluded items hub — search plus the Site / Time
 * period / Import facets. Filter state lives in the URL (nuqs), so every tab's
 * table reads the same filters.
 *
 * Filter options are sourced from the sold food list endpoint, which already
 * carries the cross-cutting facets (mirrors the purchased hub). Sold goods have
 * no Supplier or Tags facet; Customer is the sold-side sibling of Supplier.
 */

const filterOptionKeys = [
  'sites',
  'timePeriods',
  'activitiesImport',
  'customers',
] as const;

export default function ExcludedSoldItemsToolbar() {
  const { data } = useGetSoldFoodAndDrinkList();

  const filterOptions = useServerFilterOptions(
    filterOptionKeys,
    data?.filters || {}
  );

  const columns = useMemo<ColumnDef<Record<string, never>>[]>(() => {
    const sorting = { ascLabel: 'A-Z', descLabel: 'Z-A' };
    const facet = (
      id: string,
      label: string,
      options: { label: string; value: string }[]
    ): ColumnDef<Record<string, never>> => ({
      id,
      accessorFn: () => '',
      enableColumnFilter: true,
      meta: { label, variant: 'multiSelect', options, sorting },
    });

    return [
      {
        id: 'search',
        accessorFn: () => '',
        enableColumnFilter: true,
        meta: {
          label: 'Search',
          placeholder: 'Search items',
          variant: 'text',
          sorting,
        },
      },
      facet('site', 'Site', filterOptions.sitesFilter),
      facet('customer', 'Customer', filterOptions.customersFilter),
      {
        id: 'period',
        accessorFn: () => '',
        enableColumnFilter: true,
        meta: {
          label: 'Time period',
          variant: 'periodPicker',
          periods: toPickerPeriods(data?.filters?.timePeriods),
          sorting: { ascLabel: 'Oldest', descLabel: 'Newest' },
        },
      },
      facet('activitiesImport', 'Import', filterOptions.activitiesImportFilter),
    ];
  }, [filterOptions, data?.filters?.timePeriods]);

  const { table } = useServerDataTable<Record<string, never>>({
    data: [],
    columns,
    pageCount: 0,
    shallow: false,
  });

  return <DataTableToolbar table={table} />;
}
